import React from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { 
  MessageSquare, 
  FileText, 
  CheckCircle, 
  Download, 
  Clock, 
  Shield, 
  Users, 
  ArrowRight,
  PlayCircle,
  CreditCard,
  FileCheck,
  Sparkles,
  DollarSign
} from "lucide-react";

const HowItWorks = () => {
  const workflowSteps = [
    {
      number: "01",
      icon: MessageSquare,
      title: "Consultation & Questions",
      description: "Start with our AI-powered questionnaire or speak with our legal experts to understand your needs.",
      details: [
        "AI-guided questionnaire in plain English",
        "Professional consultation available",
        "State-specific requirements identified",
        "Document needs assessment",
        "Estimated timeline provided"
      ],
      image: "https://images.unsplash.com/photo-1551135049-8a33b5883817?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwyfHxjb25zdWx0YXRpb24lMjBtZWV0aW5nfGVufDB8fHx8MTc1NzM1NjkwM3ww&ixlib=rb-4.1.0&q=85",
      color: "from-teal-500 to-teal-600"
    },
    {
      number: "02", 
      icon: FileText,
      title: "AI Document Preparation",
      description: "Our advanced AI system prepares your legal documents using real legal databases and state-specific requirements.",
      details: [
        "AI-powered document generation",
        "State-specific legal compliance",
        "Real legal database integration",
        "Professional formatting applied",
        "Quality assurance checks"
      ],
      image: "https://images.unsplash.com/photo-1698047681432-006d2449c631?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwxfHxkb2N1bWVudCUyMHByZXBhcmF0aW9ufGVufDB8fHx8MTc1NzM1NjkwOHww&ixlib=rb-4.1.0&q=85",
      color: "from-teal-600 to-cyan-500"
    },
    {
      number: "03",
      icon: Shield,
      title: "Professional Review",
      description: "Expert legal professionals review your documents for accuracy, completeness, and legal compliance.",
      details: [
        "Expert legal professional review",
        "Accuracy and compliance verification",
        "Error detection and correction",
        "Legal requirements confirmation",
        "Quality assurance guarantee"
      ],
      image: "https://images.unsplash.com/photo-1562564055-71e051d33c19?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwzfHxjb25zdWx0YXRpb24lMjBtZWV0aW5nfGVufDB8fHx8MTc1NzM1NjkwM3ww&ixlib=rb-4.1.0&q=85",
      color: "from-cyan-500 to-blue-500"
    },
    {
      number: "04",
      icon: Download,
      title: "Delivery & Filing Support",
      description: "Receive your completed documents with detailed filing instructions and ongoing support.",
      details: [
        "Instant document download",
        "State-specific filing instructions",
        "Court contact information provided",
        "Deadline tracking and reminders",
        "Ongoing support available"
      ],
      image: "https://images.pexels.com/photos/6077861/pexels-photo-6077861.jpeg",
      color: "from-blue-500 to-teal-500"
    }
  ];

  const addOnServices = [
    {
      title: "Professional Legal Analysis",
      price: "$149",
      duration: "24-48 hours",
      description: "Expert review by qualified legal professionals",
      features: ["Error Detection", "Compliance Check", "Written Report", "Email Support"]
    },
    {
      title: "Express Legal Consultation", 
      price: "$179",
      duration: "4-8 hours",
      description: "Same-day priority review for urgent needs",
      features: ["Priority Processing", "Express Delivery", "Urgent Corrections"]
    },
    {
      title: "Priority Legal Support",
      price: "$99", 
      duration: "Same day",
      description: "Personalized help with court filing procedures",
      features: ["One-on-One Support", "Legal Guidance", "Court Contacts", "Deadline Reminders"]
    }
  ];

  const benefits = [
    {
      icon: DollarSign,
      title: "$4M+ in Legal Fees Saved",
      description: "Our customers have saved millions compared to traditional attorney costs"
    },
    {
      icon: Clock,
      title: "70K+ Documents Created",
      description: "Trusted by thousands of customers nationwide for their legal document needs"
    },
    {
      icon: Shield,
      title: "99.9% Accuracy Rate",
      description: "Professional-grade documents with guaranteed accuracy and compliance"
    },
    {
      icon: FileCheck,
      title: "50-State Coverage",
      description: "Documents compliant with all US state laws and regulations"
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-teal-50 to-cyan-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Badge className="mb-6 bg-teal-100 text-teal-800 hover:bg-teal-200">
            Professional Legal Document Preparation
          </Badge>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6">
            How <span className="text-teal-600">Form</span><span className="text-teal-800">Forge</span><span className="text-teal-600">Pro</span> Works
          </h1>
          <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
            Our AI-powered platform combines advanced technology with legal expertise to deliver 
            professional-grade documents at a fraction of traditional costs.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              asChild
              size="lg"
              className="bg-teal-500 hover:bg-teal-600 text-white font-semibold px-8 py-3"
            >
              <Link to="/services">Get Started Now</Link>
            </Button>
            <Button 
              asChild
              variant="outline"
              size="lg"
              className="border-teal-300 text-teal-700 hover:bg-teal-50 px-8 py-3"
            >
              <Link to="/demo">
                <PlayCircle className="mr-2 w-5 h-5" />
                Watch Demo
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Workflow Steps */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
              Our Professional Process
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From consultation to final delivery, every step is designed for accuracy, compliance, and your peace of mind.
            </p>
          </div>

          <div className="space-y-20">
            {workflowSteps.map((step, index) => (
              <div key={index} className={`flex flex-col ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} items-center gap-12`}>
                {/* Image Section */}
                <div className="flex-1 flex justify-center">
                  <div className="relative">
                    {/* Circular Image with Teal Border */}
                    <div className="w-80 h-80 rounded-full overflow-hidden border-8 border-teal-500 shadow-2xl">
                      <img 
                        src={step.image} 
                        alt={step.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    {/* Step Number Badge */}
                    <div className={`absolute -top-4 -left-4 w-16 h-16 rounded-full bg-gradient-to-r ${step.color} flex items-center justify-center text-white font-bold text-xl shadow-lg`}>
                      {step.number}
                    </div>
                  </div>
                </div>

                {/* Content Section */}
                <div className="flex-1">
                  <Card className="hover:shadow-lg transition-shadow">
                    <CardContent className="p-8">
                      <div className="flex items-center space-x-3 mb-4">
                        <div className={`p-3 rounded-full bg-gradient-to-r ${step.color}`}>
                          <step.icon className="w-6 h-6 text-white" />
                        </div>
                        <h3 className="text-2xl font-bold text-gray-800">{step.title}</h3>
                      </div>
                      
                      <p className="text-gray-600 mb-6 text-lg leading-relaxed">
                        {step.description}
                      </p>
                      
                      <div className="space-y-3">
                        {step.details.map((detail, detailIndex) => (
                          <div key={detailIndex} className="flex items-center space-x-3">
                            <CheckCircle className="w-5 h-5 text-teal-500 flex-shrink-0" />
                            <span className="text-gray-700">{detail}</span>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Add-On Services */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
              Enhanced Services Available
            </h2>
            <p className="text-xl text-gray-600">
              Add professional review and legal support to any service
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {addOnServices.map((service, index) => (
              <Card key={index} className="hover:shadow-lg transition-shadow border-teal-200">
                <CardContent className="p-6">
                  <div className="text-center mb-4">
                    <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <FileCheck className="w-6 h-6 text-teal-600" />
                    </div>
                    <h3 className="text-lg font-bold text-gray-800">{service.title}</h3>
                    <div className="text-2xl font-bold text-teal-600 mt-2">{service.price}</div>
                    <p className="text-sm text-gray-500">{service.duration}</p>
                  </div>
                  
                  <p className="text-gray-600 text-center mb-4">{service.description}</p>
                  
                  <ul className="space-y-2">
                    {service.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-center text-sm text-gray-600">
                        <CheckCircle className="w-4 h-4 mr-2 text-teal-500" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
              Why Choose FormForgePro?
            </h2>
            <p className="text-xl text-gray-600">
              Trusted results backed by proven success
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <Card key={index} className="text-center hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <benefit.icon className="w-8 h-8 text-teal-600" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-800 mb-2">{benefit.title}</h3>
                  <p className="text-sm text-gray-600">{benefit.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gradient-to-r from-teal-500 to-cyan-500 py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-teal-100 mb-8">
            Join thousands of satisfied customers who trust FormForgePro for their legal document needs.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              asChild
              size="lg"
              className="bg-white text-teal-600 hover:bg-gray-100 font-semibold px-8 py-3"
            >
              <Link to="/services">Browse Services</Link>
            </Button>
            <Button 
              asChild
              variant="outline"
              size="lg"
              className="border-white text-white hover:bg-white hover:text-teal-600 px-8 py-3"
            >
              <Link to="/pricing">View Pricing</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HowItWorks;